import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Form } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SweetAlert from 'sweetalert2';
import { Breadcrumbs, H3, H4 } from '../../AbstractElements';
import Button from '../Common/Elements/Button';
import Transparentspinner from '../Common/Loader/Transparentspinner';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../Utils/restUtils';
import { CloudLightning, Search } from 'react-feather';

const Approval = () => {
    const navigate = useNavigate();
    const authState = useSelector((state) => state.auth);

    // Core state
    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filters & Sorting state
    const [searchKeyword, setSearchKeyword] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ column: '', order: '' });

    // SEO best practice: update document title
    useEffect(() => {
        document.title = "Approvals - Attendance Correction Requests | WaterCRM";
    }, []);

    // Generate initials for avatar representation
    const getInitials = (name) => {
        if (!name) return '??';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    // Harmonious colors generated dynamically from strings
    const getAvatarColor = (name) => {
        if (!name) return '#6b7280';
        const colors = [
            '#6d28d9', // Deep Purple
            '#0f766e', // Teal
            '#b45309', // Amber
            '#1d4ed8', // Royal Blue
            '#be123c', // Rose
            '#0369a1', // Light Blue
            '#4d7c0f', // Lime
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    // Fetch correction requests from backend
    const fetchCorrectionRequests = async (page, size = perPage, search = '', status = '', sortBy = '', sortOrder = '') => {
        setLoading(true);
        try {
            const baseEndpoint = authState.userRole === 1
                ? '/admin/show-attendance-correction-request'
                : '/show-attendance-correction-request';

            const queryParams = [
                `page=${page}`,
                `page_length=${size}`,
                `search=${encodeURIComponent(search)}`,
                `status=${status}`,
                `sort_by=${sortBy}`,
                `sort_order=${sortOrder}`
            ].join('&');

            const response = await getClientUrlDataToken(authState.apiUrl, `${baseEndpoint}?${queryParams}`);

            if (response && response.status === true) {
                const serialNoStart = (page - 1) * size + 1;

                // Inspect response data formats and resolve lists robustly
                const apiDataList = response.data?.data || response.data || [];
                const finalData = Array.isArray(apiDataList) ? apiDataList : [];
                console.log("finalData", finalData)
                const transformedData = finalData.map((row, index) => ({
                    id: row.id,
                    si: serialNoStart + index,
                    employee_name: row?.employee_name || 'N/A',
                    date: row?.date || (row?.created_at ? row.created_at.split(' ')[0] : 'N/A'),
                    checkin_time: row?.current_checkin_formated || 'N/A',
                    checkout_time: row?.current_checkout_formated || 'N/A',
                    requested_checkin: row?.requested_checkin_formated || 'N/A',
                    requested_checkout: row?.requested_checkout_formated || 'N/A',
                    reason: row?.reason || row?.note || 'N/A',
                    status: row?.status || 'pending',
                }));

                setRequests(transformedData);
                setTotalRows(response.data?.total ?? finalData.length);
            } else {
                toast.error(response?.data?.message || "Failed to load attendance correction requests");
            }
        } catch (error) {
            console.error("Error loading attendance correction requests:", error);
            toast.error("An error occurred while loading requests");
        } finally {
            setLoading(false);
        }
    };

    // Trigger initial fetch
    useEffect(() => {
        fetchCorrectionRequests(1);
    }, []);

    // Handle status changes (Approve / Reject)
    const handleStatusChange = (id, newStatus) => {
        SweetAlert.fire({
            title: `Confirm ${newStatus.toLowerCase()}?`,
            text: `Are you sure you want to mark this request as ${newStatus.toLowerCase()}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ok',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
            confirmButtonColor: newStatus === 'approved' ? '#10b981' : '#dc2626',
        }).then(async (result) => {
            if (result.value) {
                setLoading(true);
                const payload = {
                    correction_request_id: id,
                    status: newStatus
                };

                const endpoint = authState.userRole === 1
                    ? '/admin/change-attendance-correction-request-status'
                    : '/change-attendance-correction-request-status';

                try {
                    const response = await postClientUrlWithToken(authState.apiUrl, endpoint, payload);
                    if (response && response.status === 200) {
                        if (response.data?.status === true) {
                            SweetAlert.fire('Success!', response.data.message || `Correction request ${newStatus.toLowerCase()} successfully`, 'success');
                            fetchCorrectionRequests(currentPage, perPage, searchKeyword, statusFilter, sortConfig.column, sortConfig.order);
                        } else {
                            toast.error(response.data?.message || "Failed to update request status");
                        }
                    } else {
                        toast.error("Failed to update request status");
                    }
                } catch (error) {
                    console.error("Error updating status:", error);
                    toast.error("An error occurred while updating request status");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Columns mapping for react-data-table-component
    const columns = useMemo(() => [
        {
            name: <H4>Sl.No</H4>,
            selector: row => row.si,
            sortable: false,
            width: '80px',
            center: true,
        },
        {
            name: <H4>Employee</H4>,
            selector: row => row.employee_name,
            sortable: true,
            id: 'employee_name',
            minWidth: '180px',
            // cell: row => {
            //     const name = row.employee_name;
            //     const initials = getInitials(name);
            //     const color = getAvatarColor(name);
            //     return (
            //         <div className="d-flex align-items-center py-2">
            //             <div style={{
            //                 width: '34px',
            //                 height: '34px',
            //                 borderRadius: '50%',
            //                 backgroundColor: color,
            //                 color: '#ffffff',
            //                 display: 'flex',
            //                 alignItems: 'center',
            //                 justifyContent: 'center',
            //                 fontWeight: '600',
            //                 marginRight: '12px',
            //                 fontSize: '12px',
            //                 boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
            //             }}>
            //                 {initials}
            //             </div>
            //             <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '13px' }}>{name}</span>
            //         </div>
            //     );
            // }
        },
        {
            name: <H4>Date</H4>,
            selector: row => row.date,
            sortable: true,
            id: 'date',
            minWidth: '120px',
            cell: row => (
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>{row.date}</span>
            )
        },
        {
            name: <H4>Current Check In</H4>,
            selector: row => row.current_checkin,
            sortable: true,
            id: 'current_checkin',
            minWidth: '160px',
            cell: row => (
                <span style={{ color: '#059669', fontWeight: '600', fontSize: '13px' }}>{row.checkin_time}</span>
            )
        },
        {
            name: <H4>Current Check Out</H4>,
            selector: row => row.current_checkout,
            sortable: true,
            id: 'current_checkout',
            minWidth: '160px',
            cell: row => (
                <span style={{ color: '#059669', fontWeight: '600', fontSize: '13px' }}>{row.checkin_time}</span>
            )
        },
        {
            name: <H4>Requested Check In</H4>,
            selector: row => row.requested_checkin,
            sortable: true,
            id: 'requested_checkin',
            minWidth: '160px',
            cell: row => (
                <span style={{ color: '#ea580c', fontWeight: '600', fontSize: '13px' }}>{row.requested_checkin}</span>
            )
        },
        {
            name: <H4>Requested Check Out</H4>,
            selector: row => row.requested_checkout,
            sortable: true,
            id: 'requested_checkout',
            minWidth: '170px',
            cell: row => (
                <span style={{ color: '#ea580c', fontWeight: '600', fontSize: '13px' }}>{row.requested_checkout}</span>
            )
        },
        {
            name: <H4>Reason</H4>,
            selector: row => row.reason,
            sortable: false,
            minWidth: '200px',
            wrap: true,

        },
        {
            name: <H4>Status</H4>,
            selector: row => <div className={`badge-${row.status}`}>{row.status}</div>,
            sortable: true,
            id: 'status',
            minWidth: '120px',
        },
        {
            name: <H4>Actions</H4>,
            sortable: false,
            minWidth: '180px',
            cell: row => (
                <div className="d-flex align-items-center gap-2 py-1">
                    {String(row.status).toLowerCase() === 'pending' ? (
                        <>
                            <Button
                                className="button-approved"
                                onClick={() => handleStatusChange(row.id, 'approved')}
                                style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600' }}
                            >
                                Approve
                            </Button>
                            <Button
                                className="button-rejected"
                                onClick={() => handleStatusChange(row.id, 'rejected')}
                                style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600' }}
                            >
                                Reject
                            </Button>
                        </>
                    ) : (
                        <span style={{ color: '#9ca3af', fontSize: '12px', fontWeight: '500', paddingLeft: '8px' }}>Processed</span>
                    )}
                </div>
            )
        }
    ], [perPage, currentPage, searchKeyword, statusFilter, sortConfig]);

    // Sorting & Pagination Handlers matching standard react-data-table-component usage
    const handleSort = (column) => {
        let newOrder = 'asc';
        if (sortConfig.column === column.id) {
            newOrder = sortConfig.order === 'asc' ? 'desc' : 'asc';
        }
        setSortConfig({ column: column.id, order: newOrder });
        fetchCorrectionRequests(currentPage, perPage, searchKeyword, statusFilter, column.id, newOrder);
    };

    const handlePageChange = page => {
        fetchCorrectionRequests(page, perPage, searchKeyword, statusFilter, sortConfig.column, sortConfig.order);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        fetchCorrectionRequests(page, newPerPage, searchKeyword, statusFilter, sortConfig.column, sortConfig.order);
        setPerPage(newPerPage);
    };

    // Filter submission handler
    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setSearchKeyword(searchInput);
        setCurrentPage(1);
        fetchCorrectionRequests(1, perPage, searchInput, statusFilter, sortConfig.column, sortConfig.order);
    };

    // Reset filters
    const handleResetFilters = () => {
        setSearchInput('');
        setSearchKeyword('');
        setStatusFilter('');
        setCurrentPage(1);
        fetchCorrectionRequests(1, perPage, '', '', sortConfig.column, sortConfig.order);
    };

    return (
        <Fragment>
            {loading && <Transparentspinner />}
            <Breadcrumbs mainTitle={'Approvals'} parent="Attendance" title={'Approvals'} />

            <div className="container-fluid">
                <Card className="shadow-sm" style={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>

                    {/* Header Section */}
                    <CardHeader style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <H3 style={{ margin: 0 }}>Attendance Correction Requests</H3>
                        {/* <Button
                            className="btn btn-primary"
                            onClick={() => navigate(`${process.env.PUBLIC_URL}/attendance/add-approval`)}
                        >
                            Add Correction 
                        </Button> */}

                    </CardHeader>

                    {/* Filter controls */}
                    <CardBody className="py-4 px-4" style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafbfc' }}>
                        <Form onSubmit={handleFilterSubmit} className="needs-validation">
                            {/* <Row className="align-items-center g-3 filterGap ps-3">
                                <Col xs="12" sm="5" md="4" lg="2">
                                    <div className="position-relative">
                                        <input
                                            id="search-input-field"
                                            className="form-control"
                                            placeholder="Search employee name..."
                                            type="text"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            style={{
                                                paddingRight: '36px',
                                                borderRadius: '8px',
                                                border: '1px solid #d1d5db',
                                                fontSize: '14px',
                                                height: '38px'
                                            }}
                                        />
                                        <Search size={16} className="text-muted" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                                    </div>
                                </Col>
                                <Col xs="12" sm="4" md="3" lg="2" className="form-element">
                                    <select
                                        id="status-select-field"
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            setCurrentPage(1);
                                            fetchCorrectionRequests(1, perPage, searchKeyword, e.target.value, sortConfig.column, sortConfig.order);
                                        }}
                                        style={{
                                            borderRadius: '8px',
                                            border: '1px solid #d1d5db',
                                            fontSize: '14px',
                                            height: '38px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </Col>
                                <Col xs="12" sm="3" md="5" lg="4" className="d-flex gap-2 custom-filtrs">
                                    <Button
                                        id="btn-filter-submit"
                                        attrBtn={{ color: "primary" }}
                                        type="submit"
                                        style={{ borderRadius: '8px', height: '38px', fontSize: '14px', fontWeight: '500' }}
                                    >
                                        Filter
                                    </Button>
                                    {(searchKeyword || searchInput || statusFilter) && (
                                        <button
                                            id="btn-filter-reset"
                                            type="button"
                                            onClick={handleResetFilters}
                                            className="btn btn-outline-light text-dark"
                                            style={{
                                                borderRadius: '8px',
                                                height: '38px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: '#ffffff'
                                            }}
                                        >
                                            Reset
                                        </button>
                                    )}
                                </Col>
                            </Row> */}
                        </Form>
                        <div className="order-history table-responsive">
                            <DataTable
                                columns={columns}
                                data={requests}
                                onSort={handleSort}
                                sortServer
                                pagination
                                paginationServer
                                defaultSortAsc
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                paginationTotalRows={totalRows}
                                paginationDefaultPage={currentPage}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onChangePage={handlePageChange}
                                fixedHeader
                                
                            />
                        </div>
                    </CardBody>

                    {/* DataTable Section */}
                </Card>
            </div>
        </Fragment>
    );
};

export default Approval;
